import { Box, Card, createStyles, Grid, makeStyles, Theme, useMediaQuery, useTheme } from '@material-ui/core';
import useNonInitialEffect from '@versiondos/hooks';
import { clear as clearProspectMessages } from 'actions/prospectMessage/prospectMessageActions';
import { clearRetrievedClientThunk } from '@spike/clients-action';
import { clearTwoWaySmsMessagesThunk, fetchTwoWaySmsCountThunk } from 'actions/twoWaySms/twoWaySmsActions';
import { PhoneSearchResult } from 'model/PhoneSearch';
import { Message, Prospect } from 'model/ProspectMessage';
import { SmsChat, SmsMessage } from 'model/TwoWaySms';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useMessagesChannelMarketplace } from 'WebSockets';
import { ProspectMessageStatus } from 'reducers/prospectMessage/prospectMessageState';
import { RootState } from 'store';
import { useAuth } from 'hooks';
import { reduceResolution, wbp } from 'Theme';
import Chat from './Chat';
import ChatIsEmpty from './Chat/ChatIsEmpty';
import NewPhoneChat from './NewPhoneChat/NewPhoneChat';
import ProspectChat from './ProspectMessage/Chat/ProspectChat';
import MarketplaceSelector, { useShowMarketplaceSelector } from './MarketplaceSelector';
import SelectionMenu from './SelectionMenu/SelectionMenu';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.down(wbp)]: {
                padding: `${reduceResolution(40)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                padding: '40px'
            },
            backgroundColor: '#F5F5F5'
        },
        marketplaceHeader: {
            flexShrink: 0,
            marginBottom: 20
        },
        container: {
            flex: 1,
            minHeight: 0,
            height: '91vh'
        },

        sideBarColumn: {
            height: '100%',
            width: '100%'
        },

        chatColumn: {
            height: '100%'
        },

        chatCard: {
            display: 'flex',
            height: '100%',
            alignItems: 'center',
            border: '1px solid #D4D4D4',
            borderRadius: '20px',
            boxShadow: 'none'
        }
    })
);

export const TwoWaySMS: FunctionComponent = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [chatActive, setChatActive] = useState(false);
    const [chatSearchActive, setChatSearchActive] = useState(true);
    const [clientSelected, setClientSelected] = useState<number | undefined>();
    const actualConversation = useSelector<RootState, Array<SmsMessage>>(state => state.twoWaySms.conversation ?? []);
    const chats = useSelector<RootState, Array<SmsChat> | undefined>(state => state.twoWaySms.chats) ?? [];
    const [receiverType, setReceiverType] = useState<'prospect' | 'customer'>('customer');
    const [prospect, setProspect] = useState<Prospect | undefined>();
    const [showNewPhone, setShowNewPhone] = useState(false);

    const location = useLocation();
    const dispatch = useDispatch();
    const classes = useStyles();
    const auth = useAuth();
    const showMarketplaceSelector = useShowMarketplaceSelector();
    const [selectedMarketplaceId, setSelectedMarketplaceId] = useState(auth.marketplaceId!);
    const { setMessagesMarketplaceOverride } = useMessagesChannelMarketplace();

    useEffect(() => {
        setMessagesMarketplaceOverride(selectedMarketplaceId);

        return () => {
            setMessagesMarketplaceOverride(undefined);
        };
    }, [selectedMarketplaceId, setMessagesMarketplaceOverride]);

    const prospectMessageStatus = useSelector<RootState, ProspectMessageStatus>(state => state.prospectMessages.status);

    const newMessage = useSelector<RootState, Message | undefined>(state => state.prospectMessages.newMessage);

    useNonInitialEffect(() => {
        if (prospectMessageStatus === ProspectMessageStatus.SaveEmptyMessageSuccess) {
            if (newMessage) {
                setChatSearchActive(true);
                setShowNewPhone(false);

                if (newMessage.type === 'customer') {
                    setReceiverType('customer');
                    setClientSelected(newMessage.id);
                } else {
                    setReceiverType('prospect');
                    setProspect({ id: newMessage.id, phone: newMessage.phone });
                }
            }
        }
    }, [prospectMessageStatus]);

    const openChatHandler = (active: boolean, client: number, chatSearchAct?: boolean) => {
        dispatch(fetchTwoWaySmsCountThunk(selectedMarketplaceId));
        setChatActive(active);
        if (client) {
            setClientSelected(client);
        }
        if (chatSearchAct) {
            setChatSearchActive(chatSearchAct);
        } else {
            actualConversation.length === 0 ? setChatSearchActive(false) : setChatSearchActive(true);
        }
    };

    React.useEffect(() => {
        if (location.search) {
            const searchParams = new URLSearchParams(location.search);
            const customerId = searchParams.get('customerId');

            if (customerId) {
                setClientSelected(Number(customerId));
            }
        }
    }, [location]);

    const handleNewChatRequest = () => {
        setChatSearchActive(false);
    };

    const handleGoBack = () => {
        setChatSearchActive(true);
        setShowNewPhone(false);
    };

    const handleClickOnPhone = (phoneSearchResult: PhoneSearchResult) => {
        setChatSearchActive(true);
        setShowNewPhone(false);
        if (phoneSearchResult.type === 'customer') {
            setClientSelected(phoneSearchResult.id);
            setReceiverType('customer');
        } else {
            setProspect(phoneSearchResult.data as Prospect);
            setReceiverType('prospect');
        }
    };

    const handleMarketplaceChange = (marketplaceId: number) => {
        setSelectedMarketplaceId(marketplaceId);
        setClientSelected(undefined);
        setProspect(undefined);
        setShowNewPhone(false);
        setChatSearchActive(true);
        setChatActive(false);
        dispatch(clearTwoWaySmsMessagesThunk());
        dispatch(clearRetrievedClientThunk());
        dispatch(clearProspectMessages());
    };

    return (
        <Box className={classes.root}>
            {showMarketplaceSelector && (
                <Box className={classes.marketplaceHeader}>
                    <MarketplaceSelector
                        selectedMarketplaceId={selectedMarketplaceId}
                        onChange={handleMarketplaceChange}
                    />
                </Box>
            )}
            <Grid
                container
                spacing={4}
                className={classes.container}
            >
                {(!isMobile || (!clientSelected && !prospect && !showNewPhone)) && (
                    <Grid
                        item
                        xs={12}
                        lg={3}
                        className={classes.sideBarColumn}
                    >
                        <SelectionMenu
                            clientSelected={clientSelected}
                            marketplaceId={selectedMarketplaceId}
                            onNewChatRequest={() => {
                                setChatSearchActive(false);
                                handleNewChatRequest();
                            }}
                            onGoBack={() => handleGoBack()}
                            onOpenChat={openChatHandler}
                            chatActive={chatActive}
                            onSearchResult={() => setChatSearchActive(false)}
                            chatSearchActive={chatSearchActive}
                            onClearSelectedClient={() => setClientSelected(undefined)}
                            setClientSelected={setClientSelected}
                            receiverType={receiverType}
                            setReceiverType={setReceiverType}
                            setProspect={setProspect}
                            selectedProspect={prospect}
                            showNewPhone={showNewPhone}
                            setShowNewPhone={setShowNewPhone}
                        />
                    </Grid>
                )}

                {(!isMobile || clientSelected || prospect || showNewPhone) && (
                    <Grid
                        item
                        xs={12}
                        lg={9}
                        className={classes.chatColumn}
                    >
                        <Card className={classes.chatCard}>
                            {showNewPhone ? (
                                <NewPhoneChat
                                    marketplaceId={selectedMarketplaceId}
                                    handleGoBack={handleGoBack}
                                    handleClickOnPhone={handleClickOnPhone}
                                />
                            ) : receiverType === 'customer' ? (
                                chats.length === 0 && clientSelected === undefined ? (
                                    <ChatIsEmpty />
                                ) : (
                                    <Chat
                                        clientSelected={clientSelected}
                                        marketplaceId={selectedMarketplaceId}
                                        setClientSelected={setClientSelected}
                                    />
                                )
                            ) : (
                                <ProspectChat
                                    prospect={prospect}
                                    marketplaceId={selectedMarketplaceId}
                                    onSaveNewClient={() => {
                                        setProspect(undefined);
                                    }}
                                    setProspect={setProspect}
                                />
                            )}
                        </Card>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default TwoWaySMS;
